import { z } from 'zod';
import * as dt from './data.types.js';
import { importedJobRefSchema, workflowAuditComparisonSnapshotSchema } from './job-workflow.schemas.js';

/**
 * Local primitives — do not import from schemas.ts (circular init via
 * `export * from './audit-run.schemas.js'` at the end of schemas.ts).
 */
const integerIdSchema = z.coerce.number().int().positive().brand('IntegerId');
const uuidSchema = z.string().brand('Uuid');
const dateSchema = z.coerce.date();

const BaseResponseSchema = z.object({
  success: z.boolean(),
  message: z.string().optional(),
});

export const auditRunStatusSchema = z.nativeEnum(dt.AUDIT_RUN_STATUS);

export const auditRunListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).optional().default(1),
  limit: z.coerce.number().int().min(1).max(100).optional().default(20),
  containerId: integerIdSchema.optional(),
  completedBy: integerIdSchema.optional(),
  runStatus: auditRunStatusSchema.optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
  search: z.string().max(255).optional(),
});


export const auditRunListItemSchema = z.object({
  id: integerIdSchema,
  tenantId: uuidSchema,
  containerId: integerIdSchema,
  containerName: z.string().nullable().optional(),
  workflowDraftId: z.string().uuid().nullable().optional(),
  completedAt: dateSchema,
  completedBy: integerIdSchema,
  completedByName: z.string().nullable().optional(),
  jobRefs: z.array(importedJobRefSchema),
  slotCountCompared: z.number().int(),
  slotCountOk: z.number().int(),
  slotCountMismatch: z.number().int(),
  slotCountGhost: z.number().int(),
  slotCountMissing: z.number().int(),
  slotCountNotCompared: z.number().int(),
  slotCountRecorded: z.number().int(),
  hasOpenConflicts: z.boolean(),
  runStatus: auditRunStatusSchema,
  openConflictCount: z.number().int().optional(),
});


export const auditRunViewSchema = auditRunListItemSchema.extend({
  comparisonSnapshot: workflowAuditComparisonSnapshotSchema,
  selectedSlotNumbers: z.array(z.number().int().min(1).max(25)),
});


export const auditRunListResponseSchema = BaseResponseSchema.extend({
  data: z.object({
    items: z.array(auditRunListItemSchema),
    total: z.number().int(),
    page: z.number().int(),
    limit: z.number().int(),
    totalPages: z.number().int(),
  }),
});

export const auditRunDetailResponseSchema = BaseResponseSchema.extend({
  data: auditRunViewSchema,
});

export const resolveConflictGroupRequestSchema = z.object({
  conflictIds: z.array(integerIdSchema).length(2),
  resolutionAction: z.enum([
    dt.RESOLUTION_ACTION.ReconcileSlotSwap,
    dt.RESOLUTION_ACTION.ReconcileOccupancy,
  ]),
  resolutionNotes: z.string().min(1).max(5000).trim(),
  linkWaferId: integerIdSchema.optional(),
  fingerprintOverride: z.string().max(500).optional(),
});


export const auditRunIdParamSchema = z.object({
  id: integerIdSchema,
});
// Inferred types
export type AuditRunIdParam = z.infer<typeof auditRunIdParamSchema>;
export type AuditRunListItem = z.infer<typeof auditRunListItemSchema>;
export type AuditRunListQuery = z.infer<typeof auditRunListQuerySchema>;
export type AuditRunView = z.infer<typeof auditRunViewSchema>;
export type ResolveConflictGroupRequest = z.infer<typeof resolveConflictGroupRequestSchema>;
